/**
 * Сумма прописью.
 *
 * Нужна не для красоты: в документе с деньгами сумма пишется дважды — цифрами
 * и словами, — и вторая запись существует ровно затем, чтобы к первой нельзя
 * было дописать ноль. План лечения на 45 000 ₽ и план на 450 000 ₽ отличаются
 * одним символом, а подписывают их у кресла, ручкой, не глядя.
 *
 * Копейки при этом пишутся цифрами: так принято в бухгалтерских формах, и
 * «семьдесят восемь копеек» никто не читает.
 */

import type { Kopecks } from './index.js';

const ONES_MALE = [
  '',
  'один',
  'два',
  'три',
  'четыре',
  'пять',
  'шесть',
  'семь',
  'восемь',
  'девять',
] as const;

/** Тысяча — женского рода: «одна тысяча», «две тысячи». */
const ONES_FEMALE = ['', 'одна', 'две', ...ONES_MALE.slice(3)] as const;

const TEENS = [
  'десять',
  'одиннадцать',
  'двенадцать',
  'тринадцать',
  'четырнадцать',
  'пятнадцать',
  'шестнадцать',
  'семнадцать',
  'восемнадцать',
  'девятнадцать',
] as const;

const TENS = [
  '',
  '',
  'двадцать',
  'тридцать',
  'сорок',
  'пятьдесят',
  'шестьдесят',
  'семьдесят',
  'восемьдесят',
  'девяносто',
] as const;

const HUNDREDS = [
  '',
  'сто',
  'двести',
  'триста',
  'четыреста',
  'пятьсот',
  'шестьсот',
  'семьсот',
  'восемьсот',
  'девятьсот',
] as const;

type Forms = readonly [one: string, few: string, many: string];

const THOUSANDS: Forms = ['тысяча', 'тысячи', 'тысяч'];
const MILLIONS: Forms = ['миллион', 'миллиона', 'миллионов'];
const BILLIONS: Forms = ['миллиард', 'миллиарда', 'миллиардов'];
const RUBLES: Forms = ['рубль', 'рубля', 'рублей'];
const KOPECKS: Forms = ['копейка', 'копейки', 'копеек'];

/**
 * Форма существительного при числе: 1 рубль, 2 рубля, 5 рублей.
 *
 * Исключение на 11–14 обязательно: «одиннадцать рубль» — тот случай, когда
 * документ читают вслух и перестают верить всему остальному.
 */
export function plural(count: number, forms: Forms): string {
  const value = Math.abs(count) % 100;
  if (value >= 11 && value <= 14) return forms[2];

  switch (value % 10) {
    case 1:
      return forms[0];
    case 2:
    case 3:
    case 4:
      return forms[1];
    default:
      return forms[2];
  }
}

/** Группа из трёх цифр словами. Род задаётся снаружи: тысячи — женские. */
function triplet(value: number, female: boolean): string[] {
  const words: string[] = [];
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;

  if (hundreds > 0) words.push(HUNDREDS[hundreds] ?? '');

  if (rest >= 10 && rest < 20) {
    words.push(TEENS[rest - 10] ?? '');
    return words;
  }

  const tens = Math.floor(rest / 10);
  const ones = rest % 10;
  if (tens > 0) words.push(TENS[tens] ?? '');
  if (ones > 0) words.push((female ? ONES_FEMALE : ONES_MALE)[ones] ?? '');

  return words;
}

const MAX_RUBLES = 999_999_999_999;

/** Целое число словами. Дальше миллиардов не идём: столько не стоит лечение. */
export function numberInWords(value: number): string {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`Прописью пишется целое неотрицательное число, получено ${String(value)}`);
  }
  if (value > MAX_RUBLES) {
    throw new Error(`Слишком большое число для записи прописью: ${String(value)}`);
  }
  if (value === 0) return 'ноль';

  const groups: { amount: number; forms: Forms | null; female: boolean }[] = [
    { amount: Math.floor(value / 1_000_000_000), forms: BILLIONS, female: false },
    { amount: Math.floor(value / 1_000_000) % 1000, forms: MILLIONS, female: false },
    { amount: Math.floor(value / 1000) % 1000, forms: THOUSANDS, female: true },
    { amount: value % 1000, forms: null, female: false },
  ];

  const words: string[] = [];
  for (const group of groups) {
    if (group.amount === 0) continue;
    words.push(...triplet(group.amount, group.female));
    if (group.forms !== null) words.push(plural(group.amount, group.forms));
  }

  return words.join(' ');
}

/**
 * Сумма прописью для печатного документа: «Сорок пять тысяч рублей 00 копеек».
 *
 * Копейки — всегда две цифры, даже нулевые: пропуск «00» оставляет в строке
 * место, куда можно дописать что угодно.
 */
export function inWords(value: Kopecks): string {
  const negative = value < 0;
  const total = Math.abs(Math.trunc(value));
  const rubles = Math.floor(total / 100);
  const kopecks = total % 100;

  const phrase = [
    negative ? 'минус ' : '',
    numberInWords(rubles),
    ' ',
    plural(rubles, RUBLES),
    ' ',
    String(kopecks).padStart(2, '0'),
    ' ',
    plural(kopecks, KOPECKS),
  ].join('');

  return phrase.charAt(0).toLocaleUpperCase('ru-RU') + phrase.slice(1);
}